'use client';

// React imports
import React, {
  useCallback,
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
// Hook imports
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import { useEditDrawerData } from 'src/hooks/common/useEditDrawerData';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useGetFilterSetsQuery, useUpdateFilterSetMutation } from 'src/api/redux/filterSets';
// Component imports
import {
  Drawer,
  Button,
  Specialties,
  SiteSpecialties,
  HospitalSystems,
  Independent,
  GPOAffiliations,
  Fellowship,
  MedicalSchools,
  Residency,
  PayerType,
  SiteOfCare,
  Inpatient,
  EncounterTags,
  CRMOwners,
  CRMStatuses,
  ProviderLists,
} from 'src/components';
// Icon imports
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { IconButton } from 'src/components/PulseUI/IconButton';

// Type imports
import { DeleteFilterModal } from 'src/components/Discovery/components/FilterDrawer/components/ManageFilters/components';

interface EntityEditDrawerProps {
  location: string;
}

const EntityEditDrawer = ({ location }: EntityEditDrawerProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();

  // RTK Query mutation hook - cache invalidation happens automatically
  const [updateFilterSet, { isLoading: isUpdating }] = useUpdateFilterSetMutation();

  const { isDrawerOpen, closeAllButCurrent, closeDrawer, getPreviousDrawerId } =
    useDrawer();

  const { getCurrentFilterSetData, clearCurrentFilterSetData } =
    useEditDrawerData();

  const { appliedSavedVolume, setSavedVolume } = useSavedVolume('discovery');
  const { refetch: refetchFilterSets } = useGetFilterSetsQuery({ type: 'CODE' });

  const {
    applyFilters,
    convertParentFilterSetToFilterSet,
    setTempSelectedFilterPreset,
    resetToApplied,
    isDirty,
    getFiltersByParentType,
  } = useFilterSets(location);

  // Get the current filter set data
  const currentFilterSet = getCurrentFilterSetData();
  const changedFilterSet = getFiltersByParentType(currentFilterSet?.type);
  useFilterSetUtils({
    filterType: 'FILTER',
  });

  // Use a ref to track if we've already initialized the filter
  const initializedRef = useRef(false);

  // Create a consistent drawer ID
  const drawerId = useMemo(
    () => `entityEditFilterDrawer-${location}`,
    [location]
  );

  // Check if drawer is open
  const isOpen = isDrawerOpen(drawerId);

  //Local State management
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Determine if there are unsaved changes
  const isFiltersDirty = useMemo(() => {
    return isDirty() || label !== currentFilterSet?.label;
  }, [label, isDirty, currentFilterSet]);

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(e.target.value);
    },
    []
  );

  // Initialize label when filter set data is available
  useLayoutEffect(() => {
    if (currentFilterSet && !initializedRef.current) {
      // Hydrate Redux filter state from the ParentFilterGroup
      convertParentFilterSetToFilterSet(currentFilterSet);
      setLabel(currentFilterSet.label || '');
      initializedRef.current = true;
    } else if (!currentFilterSet) {
      // Reset initialization when no filter set is available
      initializedRef.current = false;
      setLabel('');
    }
  }, [currentFilterSet, convertParentFilterSetToFilterSet]);

  // Reset initialization ref when drawer closes
  useLayoutEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      setLabel('');
      setError(null);
      clearCurrentFilterSetData();
    }
  }, [isOpen, clearCurrentFilterSetData]);

  const handleClose = useCallback(() => {
    // Reset all temporary filter changes back to applied state when closing without saving
    resetToApplied();

    const previousDrawerId = getPreviousDrawerId();
    if (previousDrawerId) {
      closeAllButCurrent(previousDrawerId);
    } else {
      closeDrawer(drawerId);
    }
  }, [
    resetToApplied,
    closeAllButCurrent,
    closeDrawer,
    drawerId,
    getPreviousDrawerId,
    location,
  ]);

  const handleDelete = useCallback(async () => {
    if (!currentFilterSet) return null;
    // Set the showDeleteModal state to true to open the modal
    setShowDeleteModal(true);
  }, [currentFilterSet]);

  const handleCloseDeleteModal = useCallback(() => {
    handleClose();
    setShowDeleteModal(false);
  }, [handleClose]);

  const handleSave = useCallback(async () => {
    // Validate required data
    if (!changedFilterSet || !currentFilterSet?.id) {
      setError('Missing required filter data');
      return;
    }

    // Validate filter set name
    if (!label.trim()) {
      setError('Filter set name is required');
      return;
    }

    // Reset any previous errors
    setError(null);

    try {
      const updatedFilterSetData = {
        ...changedFilterSet,
        label: label.trim(),
      };

      // Use RTK Query mutation - cache invalidation happens automatically
      await updateFilterSet({
        filterId: currentFilterSet.id,
        data: updatedFilterSetData,
      }).unwrap();

      // Create a properly typed FilterSet object for the UI update
      const newFilterSet = {
        ...currentFilterSet,
        ...changedFilterSet,
        label: label.trim(),
      };
      // Set the newly created filter set as the selected preset
      setTempSelectedFilterPreset(newFilterSet);

      // Apply the current filter state
      applyFilters();
      setNotification({
        title: 'Success',
        message: 'Filter set updated successfully',
        type: 'success',
      });

      // If this filter set is currently applied in discovery, update it with fresh data
      if (
        appliedSavedVolume &&
        appliedSavedVolume.id === currentFilterSet.id &&
        appliedSavedVolume.volumeType === 'filterSet'
      ) {
        // Get fresh filter set data from server
        const result = await refetchFilterSets();
        const freshData = result.data;
        const refreshedFilterSet = freshData?.results?.find(
          (fs) => fs.id === currentFilterSet.id
        );

        if (refreshedFilterSet) {
          const freshVolume = {
            ...refreshedFilterSet,
            volumeType: 'filterSet',
          };
          setSavedVolume(freshVolume as any);
        }
      }

      handleClose();
    } catch (error: unknown) {
      // Type guard for error object
      const err = error as { error_message?: string };
      const errorMessage = err.error_message || 'Failed to update filter set';
      console.error('Error updating filter set:', errorMessage);
      setError(errorMessage);
      log.exception(`Error updating filter set: ${err}`);
    }
  }, [
    changedFilterSet,
    currentFilterSet,
    label,
    applyFilters,
    setTempSelectedFilterPreset,
    setNotification,
    log,
    updateFilterSet,
    refetchFilterSets,
    handleClose,
    appliedSavedVolume,
    setSavedVolume,
  ]);

  const renderFilterSetContent = useCallback(() => {
    if (!currentFilterSet) return null;

    return (
      <>
        <SiteOfCare location={location} />
        <Inpatient location={location} />
        <PayerType location={location} />
        <Specialties location={location} />
        <SiteSpecialties location={location} />
        <Independent location={location} />
        <HospitalSystems location={location} />
        <GPOAffiliations location={location} />
        <MedicalSchools location={location} />
        <Residency location={location} />
        <Fellowship location={location} />
        <EncounterTags location={location} />
        <ProviderLists location={location} />
        <CRMOwners location={location} />
        <CRMStatuses location={location} />
      </>
    );
  }, [currentFilterSet, location]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      hideBackdrop
      anchor="right"
      size="base"
      data-drawer="edit-filter-drawer"
    >
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid={`entityEditFilterDrawer-${location}-header`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-lg"
              data-testid={`entityEditFilterDrawer-${location}-title`}
            >
              Edit Filter Set
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleClose}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              aria-label="Close edit filter drawer"
              data-testid={`entityEditFilterDrawer-${location}-close-button`}
            />
          </div>
        </div>
      </Drawer.Header>
      <Drawer.Content>
        <div className="self-stretch p-4 inline-flex flex-col justify-start items-start gap-6">
          {/* Filter set name input */}
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <label
              htmlFor={`entityEditFilterDrawer-${location}-filter-set-name`}
              className="self-stretch justify-start text-primary text-xs font-semibold font-['Inter'] leading-none"
            >
              Filter Set Name
            </label>
            <input
              id={`entityEditFilterDrawer-${location}-filter-set-name`}
              type="text"
              value={label}
              onChange={handleLabelChange}
              className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 w-full text-primary text-base font-normal font-['Inter'] leading-normal"
              placeholder="Enter filter set name"
              required
              aria-required="true"
              data-testid={`entityEditFilterDrawer-${location}-label`}
            />
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-4">
            <div className="justify-start text-primary text-base font-bold font-['Inter'] leading-normal">
              Filters
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              {renderFilterSetContent()}
            </div>
            {/* Error message display */}
            {error && (
              <div
                className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm"
                role="alert"
                data-testid={`entityEditFilterDrawer-${location}-error`}
              >
                {error}
              </div>
            )}
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Footer className="!p-0 w-full bg-white border-t border-neutral-300 inline-flex flex-col justify-center items-start">
        <div className="flex items-center justify-between w-full gap-2 p-4">
          <Button
            variant="soft"
            color="danger"
            size="md"
            className="w-full self-stretch px-4 py-2.5 bg-red-50 rounded-full inline-flex justify-between items-center"
            onClick={handleDelete}
            aria-label="Delete filter set"
            data-testid={`entityEditFilterDrawer-${location}-delete`}
            disabled={!currentFilterSet?.id}
          >
            Delete
          </Button>
          <Button
            variant="solid"
            color="primary"
            size="md"
            className="w-full self-stretch px-4 py-2.5 bg-sky-600 rounded-full inline-flex justify-between items-center"
            onClick={handleSave}
            loading={isUpdating}
            disabled={!isFiltersDirty}
            data-testid={`entityEditFilterDrawer-${location}-update-and-apply`}
            aria-label="Update and apply filter set"
          >
            Update & Apply
          </Button>
        </div>
      </Drawer.Footer>
      {showDeleteModal && (
        <DeleteFilterModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          filterSet={currentFilterSet}
        />
      )}
    </Drawer>
  );
};

export default EntityEditDrawer;
